import React from 'react';

import { Flex, useTheme } from '@chakra-ui/react';
import emotionStyled from '@emotion/styled';
import { Remove } from '@material-ui/icons';
import { capitalize, startCase } from 'lodash';
import { useLevelUpPageContext } from 'screens/Levelup/context';
import CompetencyActivityGraph from 'screens/oneOnOne/levelUp/competencySidePanel/components/CompetencyActivityGraph';
import CompetencyProgressBar from 'screens/oneOnOne/levelUp/competencySidePanel/components/CompetencyProgressBar';
import styled, { css } from 'styled-components';
import { Divider1, FlexContainer, StyledButton, StyledTypography, VerticallyAlignedFlexContainer } from 'styles/common';
import { baseTheme } from 'themes/clients/baseTheme';

import {
  Completed,
  Deferred,
  NeedAttention,
  OpenSidePanel,
  ProgressingWell,
  ArrowRightCircle,
} from 'assets/assetsV1/iconComponents';

import { ChevronRightIcon, StyledIcon } from 'constants/icons';
import theme from 'constants/theme';

import { Typography12, Typography13, Typography14 } from 'uiToolkit/Typography';
import { Button } from 'uiToolkitV1/Button';
import { ProgressBar } from 'uiToolkitV1/ProgressBar';
import { TooltipInfoV1 } from 'uiToolkitV1/TooltipInfoV1';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { Decrease } from '../../../../assets/assetsV1/iconComponents/Decrease';
import { Increase } from '../../../../assets/assetsV1/iconComponents/Increase';
import { PROGRESS_KEYS, TARGET_ROLE_KEYS } from '../managerTable/tableCellComponents/ProgressBar';
import LineMakerComp from './LineMakerComp';

// TODO: This component is takin too many props and has become confusing,rewrite
export const LevelUpStat = ({
  value,
  display,
  valueVariant = 'h6',
  valueColor = 'textPrimary',
  displayVariant = 'caption',
  displayColor = 'textSecondary',
  valuePositioning = 'block',
  displayPositioning = 'block',
  bottomGutter = true,
  icon,
  className,
  displayLeftGutter = 0,
  description = '',
}) => (
  <StatContainer className={className}>
    {icon}
    <StyledTypography
      bottomGutter={bottomGutter}
      customColor={valueColor}
      weight={600}
      display={valuePositioning}
      variant={valueVariant}
      style={{ marginLeft: '1rem' }}
    >
      {value}
    </StyledTypography>
    <FlexContainer style={{ flexDirection: 'column' }}>
      <StyledTypography
        customColor={displayColor}
        display={displayPositioning}
        variant={displayVariant}
        leftGutter={displayLeftGutter}
      >
        {display}
      </StyledTypography>
      <Typography12 customColor={theme.palette.text.label} leftGutter={displayLeftGutter}>
        {description}
      </Typography12>
    </FlexContainer>
  </StatContainer>
);

const StatContainer = styled.div``;

export const NotchedButton = styled(StyledButton)`
  //   width: 20ch;

  border-color: ${(props) => props.theme.palette.primary.main};
  padding: 7px 15px;

  &.Mui-disabled {
    color: ${theme.palette.text.white} !important;
    background: ${theme.palette.primary.main} !important;
  }

  ${(props) =>
    props.selected &&
    css`
      &:after {
        content: ' ';
        width: 0px;
        height: 10px;
        border-left: 9px solid transparent;
        border-right: 9px solid transparent;
        border-top: 7px solid #0088e6;
        display: inline-block;
        position: absolute;
        left: 50%;
        bottom: -10px;
        transform: translateX(-50%);
      }
    `};
`;

export const CircularIcon = styled(StyledIcon)`
  border-radius: 50%;
  border: 1px solid #eeeeee;
  padding: 1rem;
  ${(props) =>
    props.backgroundColor &&
    css`
      background-color: ${props.backgroundColor};
    `};
`;

const StyledChevronRightIcon = styled(ChevronRightIcon)`
  svg {
    margin-left: 2px;
  }
  cursor: ${(props) => (props.isClickable ? 'pointer' : 'default')};
  ${(props) =>
    props.$isExpanded &&
    css`
      transform: rotate(90deg);
    `}
`;

export const CompetencyNameCell = ({
  name = '',
  subCompetencies = [],
  setIsExpanded = () => {},
  isExpanded,
  isSubManagerRow = false,
  isLast = false,
  isExpandable = true,
}) => {
  const toggleExpandedHandler = (e) => {
    e.stopPropagation();
    setIsExpanded((prev) => !prev);
  };

  const hasSubCompetencies = Boolean(subCompetencies?.length);

  return (
    <VerticallyAlignedFlexContainer>
      {isSubManagerRow && (
        <FlexContainer style={{ paddingLeft: '11px' }}>
          <LineMakerComp isLast={isLast} />
        </FlexContainer>
      )}
      {isExpandable && (
        <ExpandIconContainer $isExpanded={isExpanded} $hasSubCompetencies={hasSubCompetencies}>
          <ArrowRightCircle fill="transparent" boxSize="16px" onClick={toggleExpandedHandler} color="primary.1565BC" />
          {isExpanded ? <DownLn $isSubManagerRow={isSubManagerRow} /> : null}
        </ExpandIconContainer>
      )}

      <TextBodyMedium
        sx={{
          marginLeft: isExpandable ? '20px' : 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '16rem',
        }}
        // title={name?.length > 20 ? name : ''}
      >
        {name}
      </TextBodyMedium>
    </VerticallyAlignedFlexContainer>
  );
};

const DownLn = emotionStyled.div`
  position: absolute;
  width: 1px;
  top: 65%;
  bottom: 0;
  background: ${(props) => props.theme.colors.text.g3};

  left: ${(props) => (props.$isSubManagerRow ? '68px' : '23.5px')};
`;

const ExpandIconContainer = styled.div`
  svg {
    transform: rotate(${(props) => (props.$isExpanded ? '90deg' : '0deg')});
    transition: transform 150ms;
  }
  ${(props) =>
    !props.$hasSubCompetencies &&
    css`
      opacity: 0.4;
      cursor: no-drop;
    `}
`;

function getDividerColor(left, score) {
  left = Number(left);
  score = Number(score);
  return left < score ? '#eee' : '#ddd';
}

export const getProgressBarColor = (status) => {
  switch (status) {
    case PROGRESS_KEYS.BUILDING:
      return baseTheme.colors.functional.FFE7C5;
    case PROGRESS_KEYS.EXCELLED:
      return baseTheme.colors.functional['91CA7D'];
    case PROGRESS_KEYS.NOT_CRITICAL:
      return baseTheme.colors.functional.FFE1D9;
    case PROGRESS_KEYS.SKILLED:
      return baseTheme.colors.functional.DBF2D3;
    case PROGRESS_KEYS.STARTING:
      return baseTheme.colors.functional.FFE7C5;
    case PROGRESS_KEYS.NOT_ENOUGH_DATA:
      return baseTheme.colors.functional.EAEAEA;
    default:
      return '';
  }
};

export const getProgressBarTextColor = (status) => {
  switch (status) {
    case PROGRESS_KEYS.BUILDING:
      return baseTheme.colors.functional.B96400;
    case PROGRESS_KEYS.EXCELLED:
      return baseTheme.colors.functional['1A5724'];
    case PROGRESS_KEYS.NOT_CRITICAL:
      return baseTheme.colors.functional.C62C02;
    case PROGRESS_KEYS.SKILLED:
      return baseTheme.colors.functional['44812F'];
    case PROGRESS_KEYS.STARTING:
      return baseTheme.colors.functional.B96400;
    case PROGRESS_KEYS.NOT_ENOUGH_DATA:
      return baseTheme.colors.text.g2;
    default:
      return '';
  }
};

export const getTargetRoleColor = (status) => {
  switch (status) {
    case TARGET_ROLE_KEYS.CURRENT_ROLE:
      return baseTheme.colors.functional.FFDE88;
    case TARGET_ROLE_KEYS.NEW_ROLE:
      return baseTheme.colors.functional['91CA7D'];
    case TARGET_ROLE_KEYS.NOT_SET:
      return baseTheme.colors.functional.FD9B9B;
    default:
      return '';
  }
};

export const DISCTRIBUTION_KEYS = {
  DETRACTORS: 'Detractors',
  PASSIVES: 'Passives',
  PROMOTORS: 'Promoters',
};

export const getTargetDistributionColor = (status) => {
  switch (status) {
    case 'red':
      return '#FD9B9B';
    case 'yellow':
      return '#FFDE88';
    case 'lightGreen':
      return '#B1E39F';
    case 'blue':
      return baseTheme.colors.functional.C62C02;
    case 'darkGreen':
      return '#91CA7D';
    default:
      return '';
  }
};

export const getTargetRoleTextColor = (status) => {
  switch (status) {
    case TARGET_ROLE_KEYS.CURRENT_ROLE:
      return baseTheme.colors.functional['8C6A00'];
    case TARGET_ROLE_KEYS.NEW_ROLE:
      return baseTheme.colors.functional['44812F'];
    case TARGET_ROLE_KEYS.NOT_SET:
      return baseTheme.colors.functional.C62C02;
    default:
      return '';
  }
};

const PeriodProgressValue = ({ progressChange }) => {
  const progressChangeValue = Math.abs(progressChange);
  if (progressChange > 0) {
    return (
      <Flex className="periodProgressChange">
        <Increase color="green" />
        <TextLabel color="green" marginLeft={1}>
          {progressChangeValue}%
        </TextLabel>
      </Flex>
    );
  }
  if (progressChange < 0) {
    return (
      <Flex className="periodProgressChange">
        <Decrease color="red" />
        <TextLabel color="red" marginLeft={1}>
          {progressChangeValue}%
        </TextLabel>
      </Flex>
    );
  }
  return null;
};
export const CompetencyProgressCell = ({
  progressBar = {},
  progress = null,
  barHeight = '0.5rem',
  levelledUpText = '',
  color,
  percentage = 0,
  showPercentage,
  containerMaxWidth = '280px',
}) => {
  const derivedProgress = percentage > 1 ? progress : PROGRESS_KEYS.NOT_ENOUGH_DATA;
  const derivedShowPercentage = percentage > 1 ? showPercentage : false;

  return (
    <ProgressBarContainer>
      <Flex alignItems="center" gap={1} maxW={containerMaxWidth}>
        <ProgressBar
          text={capitalize(startCase(derivedProgress ?? ''))}
          value={percentage}
          size="large"
          color={color ?? getProgressBarColor(derivedProgress)}
          textProps={{ sx: { textTransform: 'capitalize' } }}
          textColor={getProgressBarTextColor(derivedProgress)}
          showPercentage={false}
        />
        <Flex w="80px" alignItems="center">
          {derivedShowPercentage && (
            <TextBodyMedium minW="20px" bold>
              {percentage}%
            </TextBodyMedium>
          )}
          <TooltipInfoV1
            ml="6px"
            title="Maven calculates the progress on the basis of quality, relevance and frequency of feedback received for this competency"
          />
        </Flex>
      </Flex>
      {Boolean(levelledUpText) && (
        <TextLabel color={baseTheme.colors.text.g2} sx={{ marginTop: '4px' }}>
          {levelledUpText}
        </TextLabel>
      )}
    </ProgressBarContainer>
  );
};

export const CompetencyPeriodProgressCell = ({
  progressBar = {},
  progress = null,
  barHeight = '0.5rem',
  levelledUpText = '',
  color,
  percentage = 0,
  showPercentage,
}) => {
  const { periodProgressChange } = percentage ?? {};

  return (
    <Flex
      w="80%"
      sx={{
        div: {
          width: '80%',
        },
        '.periodProgressChange': {
          width: 'auto',
        },
      }}
    >
      <ProgressBar
        text={capitalize(startCase(percentage?.progressStatus ?? ''))}
        value={percentage.progressPercentage}
        size="large"
        color={color ?? getProgressBarColor(progress)}
        textProps={{ sx: { textTransform: 'capitalize' } }}
        textColor={getProgressBarTextColor(progress)}
        showPercentage={showPercentage}
      />
      <PeriodProgressValue progressChange={periodProgressChange} />

      {Boolean(levelledUpText) && (
        <TextLabel color={baseTheme.colors.text.g2} sx={{ marginTop: '4px' }}>
          {levelledUpText}
        </TextLabel>
      )}
    </Flex>
  );
};

const ProgressBarContainer = styled.div``;

export const CompetencyPeriodSelectCell = ({ targetPeriodStart, competencyId }) => {
  const {
    state: { levelUpTargetDates, isTargetRoleSelected, levelUpDetails },
    handleCompetencyTargetDateChange = () => {},
  } = useLevelUpPageContext();

  const { currentJobFunction, currentJobRole, targetJobFunction, targetJobRole } = levelUpDetails ?? {};

  const handleTargetDateChange = ({ competencyId, targetPeriodStart, subCompetencyId }) => {
    const jobFunctionId = isTargetRoleSelected ? targetJobFunction?.uuid : currentJobFunction?.uuid;
    const jobRoleId = isTargetRoleSelected ? targetJobRole?.uuid : currentJobRole?.uuid;
    handleCompetencyTargetDateChange({
      jobFunctionId,
      jobRoleId,
      competencyId,
      targetPeriodStart,
      subCompetencyId,
    });
  };

  return <TextBodyMedium color="text.g1">{targetPeriodStart?.periodName ?? 'No Date'}</TextBodyMedium>;

  // return (
  //   <FlexContainer style={{ justifyContent: 'center' }}>
  //     <CompetencyPeriodSelect
  //       competencyId={competencyId}
  //       selectedPeriod={targetPeriodStart}
  //       levelUpTargetDates={levelUpTargetDates}
  //       handleTargetDateChange={handleTargetDateChange}
  //     />
  //   </FlexContainer>
  // );
};

export const LevelUpStatusRowForTooltip = ({ progress, label, description, showDivider = false, index }) => (
  <div>
    <VerticallyAlignedFlexContainer>
      <Typography14 color={theme.palette.text.blackv1} weight={600}>
        {label}
      </Typography14>
      <div style={{ width: '8rem', marginLeft: 'auto' }}>
        <CompetencyProgressBar
          filledBlocks={index}
          totalBlocks={4}
          progress={progress}
          barHeight="0.375rem"
          percentage={index < 2 ? index : (index - 1) * 25 + 1}
        />
      </div>
    </VerticallyAlignedFlexContainer>
    <Typography13 color={theme.palette.text.blackv1}>{description}</Typography13>
    {showDivider && <Divider1 style={{ margin: '1rem 0' }} />}
  </div>
);

export const LevelUpActivityGraph = ({ activity = [], competencyId }) => (
  <CompetencyActivityGraph
    data={activity}
    competencyId={competencyId}
    margin={{ top: 10, right: 20, left: 10, bottom: -20 }}
  />
);

const riskEstimationKeys = {
  NOT_APPLICABLE: 'NOT_APPLICABLE',
  NEEDS_ATTENTION: 'NEEDS_ATTENTION',
  PROGRESSING_WELL: 'PROGRESSING_WELL',
  DEFERRED_FOR_LATER: 'DEFERRED_FOR_LATER',
  COMPLETED: 'COMPLETED',
};

export const getRiskEstimationIcon = ({ risk }) => {
  switch (risk) {
    case riskEstimationKeys.NOT_APPLICABLE:
      return Remove;
    case riskEstimationKeys.NEEDS_ATTENTION:
      return NeedAttention;
    case riskEstimationKeys.PROGRESSING_WELL:
      return ProgressingWell;
    case riskEstimationKeys.DEFERRED_FOR_LATER:
      return Deferred;
    case riskEstimationKeys.COMPLETED:
      return Completed;
    default:
      return Remove;
      break;
  }
};

export const getRiskEstimationColor = ({ risk }) => {
  switch (risk) {
    case riskEstimationKeys.NOT_APPLICABLE:
      return theme.palette.grey.dark;
    case riskEstimationKeys.NEEDS_ATTENTION:
      return baseTheme.colors.functional.C62C02;
    case riskEstimationKeys.PROGRESSING_WELL:
      return baseTheme.colors.functional['44812F'];
    case riskEstimationKeys.DEFERRED_FOR_LATER:
      return baseTheme.colors.functional['8C6A00'];
    case riskEstimationKeys.COMPLETED:
      return baseTheme.colors.functional['1A5724'];
    default:
      break;
  }
};

const ViewCompetencyButton = ({ onClick = () => {}, ...props }) => (
  <div {...props}>
    <Button StartIcon={OpenSidePanel} variant="rowButtonOutlined" onClick={onClick}>
      View Competency
    </Button>
  </div>
);

export const LevelUpRiskEstimationCell = ({ displayName, risk }) => {
  const color = getRiskEstimationColor({ risk });
  const iconBGColor = `${color}30`;
  const IconComponent = getRiskEstimationIcon({ risk });

  return (
    <>
      <VerticallyAlignedFlexContainer>
        {/* <StyledIconV2
        containerSize="fixed24px"
        size="fixed16px"
        circular
        color={color}
        bgColor={iconBGColor}
      > */}
        <IconComponent color={color} />
        {/* </StyledIconV2> */}

        <TextBodyMedium sx={{ marginLeft: '6px' }} color={theme.palette.text.blackv1}>
          {displayName}
        </TextBodyMedium>
      </VerticallyAlignedFlexContainer>
      <ViewCompetencyButton className="showOnHover" style={{ position: 'absolute', top: '25%', right: '24px' }} />
    </>
  );
};

export const LevelUpRiskEstimationCountCell = ({ riskCount }) => {
  // const {
  //   state: { riskEstimationList = [] }
  // } = useLevelUpPageContext();

  const theme = useTheme();

  const riskEstimation = Object?.keys(riskCount ?? {});

  const percentage = riskCount ? riskCount[riskEstimation[1]] : 0;

  // const { risk } = riskEstimationList?.find(({ risk }) => risk === riskEstimation[0]) ?? {};

  // const color = getRiskEstimationColor({ risk });

  const displayName = riskCount ? 'Needs Attention' : 'No Attention Needed';

  return (
    <Flex flexDirection="column">
      <TextBodyMedium
        bold
        // color={color}
      >
        {percentage}%
      </TextBodyMedium>

      <TextLabel color={theme.colors.text.g2}>{displayName}</TextLabel>
    </Flex>
  );
};

export const ProgressPercentageCell = ({ value }) => (
  <FlexContainer style={{ justifyContent: 'center', width: '100%' }}>
    <Typography13>{value}%</Typography13>
  </FlexContainer>
);
